import Phaser from 'phaser';
import type { AnimalKey, CharacterConfig, Difficulty, RunStats } from '../types';
import { ANIMAL_STATS } from '../config/animals';
import { DEFAULT_CHARACTER } from '../config/presets';

const KEYS = {
  character: 'character',
  animal: 'animal',
  level: 'level',
  run: 'runStats',
  difficulty: 'difficulty',
} as const;

export interface DifficultyMods {
  speedMult: number;     // multiplied into runner speed
  hearts: number;        // starting hearts
  bossHpMult: number;    // multiplied into boss HP
  bossDmgMult: number;   // multiplied into boss damage
}

export const DIFFICULTY_MODS: Record<Difficulty, DifficultyMods> = {
  easy:   { speedMult: 0.85, hearts: 4, bossHpMult: 0.7, bossDmgMult: 0.6 },
  medium: { speedMult: 1.0,  hearts: 3, bossHpMult: 1.0, bossDmgMult: 1.0 },
  hard:   { speedMult: 1.2,  hearts: 2, bossHpMult: 1.3, bossDmgMult: 1.4 },
};

type Reg = Phaser.Data.DataManager;

export const GameState = {
  getCharacter(reg: Reg): CharacterConfig {
    return (reg.get(KEYS.character) as CharacterConfig | undefined) ?? DEFAULT_CHARACTER;
  },
  setCharacter(reg: Reg, c: CharacterConfig): void {
    reg.set(KEYS.character, c);
  },

  getAnimal(reg: Reg): AnimalKey {
    return (reg.get(KEYS.animal) as AnimalKey | undefined) ?? 'horse';
  },
  setAnimal(reg: Reg, a: AnimalKey): void {
    reg.set(KEYS.animal, a);
  },

  getLevel(reg: Reg): number {
    return (reg.get(KEYS.level) as number | undefined) ?? 1;
  },
  setLevel(reg: Reg, n: number): void {
    reg.set(KEYS.level, n);
  },
  advanceLevel(reg: Reg): number {
    const next = this.getLevel(reg) + 1;
    this.setLevel(reg, next);
    return next;
  },

  getRunStats(reg: Reg): RunStats {
    const animalKey = this.getAnimal(reg);
    const maxHp = ANIMAL_STATS[animalKey].hp;
    return (
      (reg.get(KEYS.run) as RunStats | undefined) ?? {
        fruitsThisLevel: 0,
        hp: maxHp,
        maxHp,
      }
    );
  },
  setRunStats(reg: Reg, r: RunStats): void {
    reg.set(KEYS.run, r);
  },
  resetRunStats(reg: Reg): void {
    const animalKey = this.getAnimal(reg);
    const maxHp = ANIMAL_STATS[animalKey].hp;
    reg.set(KEYS.run, { fruitsThisLevel: 0, hp: maxHp, maxHp });
  },

  getDifficulty(reg: Reg): Difficulty {
    return (reg.get(KEYS.difficulty) as Difficulty | undefined) ?? 'medium';
  },
  setDifficulty(reg: Reg, d: Difficulty): void {
    reg.set(KEYS.difficulty, d);
  },
};
